import { useMemo } from 'react';
import { useResourceSets } from 'components/IAP/hooks/resourceSetHooks';
import { useOrgActions } from 'components/IAP/hooks/orgActionHooks';
import { ORGANIZATION } from 'components/IAP/ICSConstants';
import { useListLayerNodes } from 'hooks/layerNode/useListLayerNodes';
import { useGroupGuid } from 'components/IAP/hooks/commonHooks';
import type { ReportMentionItem } from './reportMentionTypes';

/** Org Resources + org Actions + map layers for report section @-mention suggestions. */
export function useReportMentionItems(): {
  items: ReportMentionItem[];
  isLoading: boolean;
} {
  const { data: resourceSets = [], isLoading: resourcesLoading } =
    useResourceSets();
  const { data: orgActions = [], isLoading: actionsLoading } =
    useOrgActions(ORGANIZATION);
  const groupGuid = useGroupGuid();
  const { data: layerTree, isLoading: layersLoading } = useListLayerNodes(
    { filter: { groupGuid: groupGuid ?? '' } },
    { enabled: !!groupGuid }
  );

  const items = useMemo(() => {
    const resources: ReportMentionItem[] = resourceSets
      .filter((set) => !set.archived)
      .map((set) => ({
        kind: 'resource' as const,
        id: String(set.resource_set_id || set.id),
        label: set.name || 'Untitled resource',
        subtitle: [set.kind, set.type].filter(Boolean).join(' · ') || undefined,
      }));

    const actions: ReportMentionItem[] = orgActions
      .filter((action) => !action.archived)
      .map((action) => ({
        kind: 'action' as const,
        id: String(action.action_id || action.id),
        label: action.name || 'Untitled action',
        subtitle: action.status || undefined,
      }));

    const layers: ReportMentionItem[] = [];
    for (const group of layerTree?.data ?? []) {
      for (const item of group.items) {
        const layer = item.layer;
        if (!layer?.id) continue;
        const isExternal = 'serviceUrl' in layer;
        layers.push({
          kind: 'layer',
          id: String(layer.id),
          label: layer.name || 'Untitled layer',
          subtitle: isExternal ? 'External' : 'Internal',
          layerType: isExternal ? 'external' : 'internal',
        });
      }
    }

    return [...resources, ...actions, ...layers];
  }, [layerTree?.data, orgActions, resourceSets]);

  return {
    items,
    isLoading: resourcesLoading || actionsLoading || layersLoading,
  };
}
